import {
  ShieldCheck,
  Lock,
  Eye,
  Palette,
  Wallet,
  Zap,
  Receipt,
  Smartphone,
  PackageCheck,
  CheckCircle2,
  Webhook,
  Code2,
  type LucideIcon,
} from 'lucide-react';

interface FeatureItem {
  icon: LucideIcon;
  title: string;
  desc: string;
}

const FEATURE_CATEGORIES: { name: string; features: FeatureItem[] }[] = [
  {
    name: 'Hosted Checkout',
    features: [
      { icon: Palette, title: 'Custom Branded Checkout', desc: 'Configure logo, colors, fonts, and copy from your dashboard. Your brand on every payment page.' },
      { icon: Smartphone, title: 'Mobile Wallet Deep-Links', desc: 'Tap-to-pay with Phantom, Solflare, or any Solana Pay-compatible wallet. QR fallback for desktop.' },
      { icon: CheckCircle2, title: 'Step-by-Step Buyer Guide', desc: 'On-screen instructions walk first-time crypto buyers through the payment in plain language.' },
    ],
  },
  {
    name: 'Settlement',
    features: [
      { icon: Wallet, title: 'Non-Custodial Solana Wallet', desc: 'Generated in your browser during onboarding. You hold the seed. We never see it.' },
      { icon: Zap, title: 'Direct USDC Settlement', desc: 'Funds settle to your wallet on the Solana blockchain in seconds. No custodian. No holds.' },
      { icon: Receipt, title: 'Transparent Platform Fee', desc: 'A flat 4% platform fee is included in the on-chain transaction. The customer signs once.' },
    ],
  },
  {
    name: 'Integration',
    features: [
      { icon: PackageCheck, title: 'WooCommerce Plugin', desc: 'Drop-in plugin for any WooCommerce store. Install, paste your API key, and accept USDC.' },
      { icon: Webhook, title: 'HMAC-Signed Webhooks', desc: 'SHA-256 signed callbacks confirm payment to your store. Constant-time comparison on every event.' },
      { icon: Code2, title: 'Automatic Order Completion', desc: 'When the on-chain payment is verified, your order moves to complete automatically. No reconciliation.' },
    ],
  },
  {
    name: 'Verification & Visibility',
    features: [
      { icon: ShieldCheck, title: 'On-Chain Verification', desc: 'Every payment is parsed from Solana and validated against the expected amount before completion.' },
      { icon: Lock, title: 'Public Blockchain Receipts', desc: 'Every transaction is permanent and verifiable on Solana. Nothing to dispute, nothing to lose.' },
      { icon: Eye, title: 'Real-Time Merchant Dashboard', desc: 'Live order status, wallet balance, and transaction history. WebSocket-powered, no manual refresh.' },
    ],
  },
];

export default function FeaturesSection() {
  return (
    <section id="features" className="bg-navy-950 py-14 sm:py-20 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <span className="font-mono text-[10px] tracking-widest uppercase text-bone-white/30">
            Section II &mdash; Features
          </span>
          <div className="mt-2 w-full h-px bg-navy-800/50" />
        </div>

        <h2 className="font-serif text-bone-white text-3xl md:text-4xl mb-4">
          Built for crypto-first commerce.
        </h2>
        <p className="font-sans text-navy-200/70 text-sm sm:text-base max-w-2xl mb-10 sm:mb-16 leading-relaxed">
          Everything BonesPay does today, in plain language. Nothing more.
        </p>

        <div className="space-y-12">
          {FEATURE_CATEGORIES.map((cat) => (
            <div key={cat.name}>
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-xs text-navy-300 uppercase tracking-widest">{cat.name}</span>
                <div className="flex-1 h-px bg-navy-800/50" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-navy-800/30">
                {cat.features.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="bg-navy-900/50 p-5 hover:bg-navy-800/40 transition-colors">
                    <Icon size={18} strokeWidth={1.5} className="text-navy-300 mb-3" />
                    <h3 className="font-sans text-sm font-medium text-bone-white mb-1.5">
                      {title}
                    </h3>
                    <p className="font-sans text-xs text-navy-300/80 leading-relaxed">
                      {desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
